'use client'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import React, { useState, useEffect, useRef, useTransition } from 'react'
import { cn } from '@/utilities/ui'
import { useDebounce } from '@/utilities/useDebounce'
import { useRouter } from 'next/navigation'

type SearchProps = {
  initialValue?: string
  inputClassName?: string
}

export const Search: React.FC<SearchProps> = ({ initialValue = '', inputClassName }) => {
  const [value, setValue] = useState(() => initialValue)
  const router = useRouter()

  const debouncedValue = useDebounce(value)
  const lastInitialValueRef = useRef(initialValue)
  const lastSyncedQueryRef = useRef(initialValue.trim())
  const isTypingRef = useRef(false)
  const valueRef = useRef(initialValue)
  const [, startTransition] = useTransition()

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    if (initialValue !== lastInitialValueRef.current) {
      lastInitialValueRef.current = initialValue
      lastSyncedQueryRef.current = initialValue.trim()

      if (!isTypingRef.current || initialValue === valueRef.current) {
        setValue(initialValue)
        isTypingRef.current = false
      }
    }
  }, [initialValue])

  useEffect(() => {
    const trimmed = debouncedValue.trim()
    if (trimmed === lastSyncedQueryRef.current) {
      return
    }

    lastSyncedQueryRef.current = trimmed

    const params = new URLSearchParams()

    if (trimmed) {
      params.set('q', trimmed)
    }

    const queryString = params.toString()

    startTransition(() => {
      router.replace(`/search${queryString ? `?${queryString}` : ''}`, { scroll: false })
    })

    isTypingRef.current = false
  }, [debouncedValue, router])

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <Label htmlFor="search" className="sr-only">
          Search
        </Label>
        <Input
          id="search"
          value={value}
          onChange={(event) => {
            isTypingRef.current = true
            setValue(event.target.value)
          }}
          placeholder="Search"
          type="search"
          autoComplete="off"
          className={cn('h-12 rounded-full border-transparent text-base shadow-sm', inputClassName)}
        />
        <button type="submit" className="sr-only">
          submit
        </button>
      </form>
    </div>
  )
}
